// src/pages/public/Catalog.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { fetchDesignsCached, fetchDesignsPaginated, fetchBanners } from "../../firebase/services";
import { useBranding } from "../../hooks/useBranding";
import { useData } from "../../hooks/useData";
import { useMobile } from "../../hooks/useMobile";
import BannerSlider from "../../components/BannerSlider";
import { ProductCard, ProductModal } from "../../components/ProductCard";
import { B, fonts, Spinner, Divider } from "../../components/UI";
import SiteFooter from "../../components/SiteFooter";
import StickyCallButton from "../../components/StickyCallButton";
import { withCloudinaryAutoFormatQuality, withCloudinaryCategoryTint } from "../../utils/cloudinary";

const PAGE_SIZE = 20;

export default function Catalog() {
  const { branding } = useBranding();
  const { categories } = useData();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [designs, setDesigns] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Pagination state
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [allDesignsFetched, setAllDesignsFetched] = useState(false);

  // Sentinel ref for infinite scroll
  const sentinelRef = useRef(null);

  // Initial fetch — paginated
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [paginated, bannersData] = await Promise.all([
        fetchDesignsPaginated(PAGE_SIZE),
        fetchBanners(),
      ]);
      if (cancelled) return;
      setDesigns(paginated.designs);
      setLastDoc(paginated.lastDoc);
      setHasMore(paginated.hasMore);
      setBanners(bannersData);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load more designs (infinite scroll)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || allDesignsFetched) return;
    setLoadingMore(true);
    try {
      const paginated = await fetchDesignsPaginated(PAGE_SIZE, lastDoc);
      setDesigns(prev => [...prev, ...paginated.designs]);
      setLastDoc(paginated.lastDoc);
      setHasMore(paginated.hasMore);
    } catch (err) {
      console.error("Failed to load more designs:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, allDesignsFetched, lastDoc]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (loading || allDesignsFetched) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "400px 0px" } // fire 400px before reaching the sentinel
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, allDesignsFetched, loadMore]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const accent = branding.primaryColor || B.orange;
  const active = designs.filter(d => d.active && d.visibleOnWebsite === true);
  // const featured = active.filter(d => d.badge === "featured" || d.badge === "new").slice(0, 4);
  // const newArrivals = active.filter(d => d.badge === "new").slice(0, 10);
  const bestsellers = active.filter(d => d.badge === "bestseller").slice(0, 8);

  const q = search.trim().toLowerCase();

  // Category matches for suggestions
  const matchedCategories = q.length > 1
    ? categories.filter(c =>
      c.name?.toLowerCase().includes(q) || c.slug?.toLowerCase().includes(q)
    )
    : [];

  // Product matches — search across all designs if we have them all, otherwise fetch all
  const [searchedDesigns, setSearchedDesigns] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (q.length <= 1) {
      setSearchedDesigns(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      // If we haven't fetched all designs yet, fetch them now for comprehensive search
      let allDesigns;
      if (!allDesignsFetched) {
        allDesigns = await fetchDesignsCached();
        if (cancelled) return;
        setAllDesignsFetched(true);
      } else {
        allDesigns = designs;
      }

      const matched = allDesigns.filter(d =>
        d.active && d.visibleOnWebsite === true &&
        [d.name, d.designNo, d.colors, d.fabric, d.comboType, d.work]
          .some(f => f?.toLowerCase().includes(q))
      );
      if (cancelled) return;
      setSearchedDesigns(matched);
      setSearchLoading(false);
    })();

    return () => { cancelled = true; };
  }, [q, allDesignsFetched]);

  const searched = searchedDesigns;

  const whatsappLink = branding.whatsapp
    ? `https://wa.me/${branding.whatsapp.replace(/\D/g, "")}?text=Hi! I'm interested in your kurti collection.`
    : null;

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && matchedCategories.length === 1) {
      navigate(`/category/${matchedCategories[0].slug}`);
      setSearch("");
      setShowSuggestions(false);
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Kurti Manufacturer in Ahmedabad | Wholesale Kurtis Supplier | {branding.brandName}
        </title>

        <meta
          name="description"
          content={`Leading kurti manufacturer in Ahmedabad - ${branding.brandName}. We specialize in wholesale cotton kurtis, designer kurtis, 2pcs & 3pcs sets, and coord sets. Bulk orders available across India. Call ${branding.phone}.`}
        />

        <meta
          name="keywords"
          content="kurti manufacturer Ahmedabad, wholesale kurtis Ahmedabad, cotton kurtis supplier Gujarat, designer kurtis manufacturer India, ladies kurti wholesale, 2pcs kurti set, 3pcs kurti set, coord sets manufacturer, Nilam Synthetics"
        />

        <meta name="robots" content="index, follow" />

        {/* Canonical */}
        <link
          rel="canonical"
          href={process.env.REACT_APP_SITE_URL || window.location.origin}
        />

        {/* Open Graph (Social Sharing) */}
        <meta
          property="og:title"
          content={`Kurti Manufacturer in Ahmedabad | ${branding.brandName}`}
        />
        <meta
          property="og:description"
          content={`Wholesale kurti manufacturer in Ahmedabad. Explore premium cotton & designer kurtis. Contact ${branding.phone}`}
        />
        <meta
          property="og:url"
          content={process.env.REACT_APP_SITE_URL || window.location.origin}
        />
        <meta property="og:type" content="website" />

        {/* Local SEO Boost */}
        <meta name="geo.region" content="IN-GJ" />
        <meta name="geo.placename" content="Ahmedabad" />
        <meta name="geo.position" content="23.0225;72.5714" />
        <meta name="ICBM" content="23.0225, 72.5714" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ClothingStore",
            "@id": `${process.env.REACT_APP_SITE_URL || window.location.origin}#store`,
            "name": branding.brandName,
            "url": process.env.REACT_APP_SITE_URL || window.location.origin,
            "description": "Kurti manufacturer in Ahmedabad specializing in wholesale ethnic wear and designer kurtis.",
            "telephone": branding.phone,
            "image": `${process.env.REACT_APP_SITE_URL || window.location.origin}/logo.png`,
            "priceRange": "100 - 1000",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6, 9, 10, A K Shah Trust Building, Extra Gali, Sindhi Market, Revdi Bazar, Kalupur",
              "addressLocality": "Ahmedabad",
              "addressRegion": "Gujarat",
              "postalCode": "380002",
              "addressCountry": "IN"
            },
            "areaServed": "IN",
            "sameAs": [
              "https://www.youtube.com/@NilamSynthetics",
              "https://www.instagram.com/nilamsynthetics"
            ]
          })}
        </script>
      </Helmet>

      <main>
        {/* ── Banner Slider ── */}
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 24px 0" }}>
          {banners.filter(b => b.active).length > 0 && (
            <BannerSlider banners={banners} />
          )}
        </div>

        <section style={{ maxWidth: 1280, margin: "0 auto", padding: "22px 24px 8px" }}>
          <h1 style={{ margin: 0, fontFamily: fonts.display, fontSize: "clamp(26px,4vw,40px)", color: B.text, lineHeight: 1.2 }}>
            Kurti Manufacturer in Ahmedabad - {branding.brandName}
          </h1>
          <h2 style={{ margin: "10px 0 0", fontFamily: fonts.display, fontSize: "clamp(18px,3vw,24px)", color: B.textMid, fontWeight: 600 }}>
            Wholesale kurtis, coord sets and ethnic wear with fast assistance.
          </h2>
          <h3 style={{ margin: "8px 0 0", fontFamily: fonts.body, fontSize: 13, color: B.textLight, fontWeight: 500 }}>
            Browse category-wise collections and connect directly on call or WhatsApp.
          </h3>
        </section>

        {/* ── Category Grid ── */}
        <>
          <section style={{ maxWidth: 1280, margin: "0 auto", padding: "36px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ fontFamily: fonts.display, fontSize: 26, fontWeight: 700, color: B.text }}>
                Shop by Category
              </h2>
            </div>
            <div className="cat-grid">
              {categories.map((cat, i) => (
                <CategoryTile key={cat.id} cat={cat} accent={accent} delay={i * 60} onClick={() => navigate(`/category/${cat.slug}`)} />
              ))}
            </div>
          </section>

          <Divider />
        </>

        {/* Search bar */}
        <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ maxWidth: 560, margin: "0 auto", position: "relative" }} ref={searchRef}>
            <span style={{ position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: B.textLight, pointerEvents: "none" }}>🔍</span>
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleSearchSubmit}
              placeholder="Search designs, colors, categories..."
              style={{ width: "100%", background: B.orangeFaint, border: `2px solid ${search ? accent : "transparent"}`, borderRadius: 50, padding: "12px 44px 12px 48px", fontSize: 14, color: B.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box", transition: "border 0.2s" }}
            />
            {search && (
              <button onClick={() => { setSearch(""); setShowSuggestions(false); }}
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: B.textLight, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>
                ×
              </button>
            )}

            {/* Category suggestions dropdown */}
            {showSuggestions && matchedCategories.length > 0 && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: B.white, borderRadius: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.14)", border: `1px solid ${B.borderLight}`, overflow: "hidden", zIndex: 200 }}>
                <div style={{ padding: "8px 16px 6px", fontSize: 10, fontWeight: 700, color: B.textLight, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>
                  Categories
                </div>
                {matchedCategories.map(cat => (
                  <button key={cat.id}
                    onClick={() => { navigate(`/category/${cat.slug}`); setSearch(""); setShowSuggestions(false); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = B.orangeFaint}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <span style={{ fontSize: 20 }}>{cat.icon || "📁"}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: B.text, fontFamily: fonts.display }}>{cat.name}</div>
                      <div style={{ fontSize: 11, color: B.textLight, fontFamily: fonts.body }}>/category/{cat.slug}</div>
                    </div>
                    <span style={{ marginLeft: "auto", color: accent, fontSize: 18, lineHeight: 1 }}>→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Search results */}
        {searched !== null && (
          <section style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: B.text, margin: 0 }}>
                Search Results
                <span style={{ fontSize: 14, fontWeight: 400, color: B.textLight, marginLeft: 10, fontFamily: fonts.body }}>
                  "{search}" — {searchLoading ? "searching..." : `${searched.length} design${searched.length !== 1 ? "s" : ""}`}
                </span>
              </h2>
              <button onClick={() => { setSearch(""); setShowSuggestions(false); }}
                style={{ background: "none", border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "6px 14px", color: B.textMid, fontSize: 12, cursor: "pointer", fontFamily: fonts.body }}>
                Clear
              </button>
            </div>

            {/* Category quick-links if any matched */}
            {matchedCategories.length > 0 && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
                {matchedCategories.map(cat => (
                  <button key={cat.id}
                    onClick={() => { navigate(`/category/${cat.slug}`); setSearch(""); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, background: accent + "18", border: `1.5px solid ${accent}44`, borderRadius: 20, padding: "7px 16px", cursor: "pointer", fontFamily: fonts.body, fontSize: 13, fontWeight: 600, color: accent }}>
                    {cat.icon && <span>{cat.icon}</span>}
                    View {cat.name} →
                  </button>
                ))}
              </div>
            )}

            {searchLoading ? (
              <Spinner full />
            ) : searched.length === 0 && matchedCategories.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 24px", color: B.textLight, fontFamily: fonts.body }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                No results for "{search}"
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 24 }}>
                {searched.map((d, i) => (
                  <div key={d.id} style={{ animation: `fadeUp 0.4s ${i * 40}ms both` }}>
                    <ProductCard design={d} onClick={setSelected} whatsapp={branding.whatsapp} />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── New Arrivals ── */}
        {/* {!searched && newArrivals.length > 0 && (
          <DesignSection
            title="New Arrivals" subtitle="Fresh designs, just added"
            designs={newArrivals} accent={accent}
            onSelect={setSelected} whatsapp={branding.whatsapp}
            onViewAll={() => navigate("/category/all?filter=new")}
          />
        )} */}

        {/* ── Latest Designs ── */}
        {!searched && active.length > 0 && (
          <DesignSection
            title="Latest Designs" subtitle="All designs, newest first"
            designs={active} accent={accent}
            onSelect={setSelected} whatsapp={branding.whatsapp}
            onViewAll={() => navigate("/category/all")}
          />
        )}

        {/* ── Category Previews ── */}
        {/* !searched && categories.slice(0, 6).map(cat => {
          const catDesigns = active.filter(d => d.categorySlug === cat.slug || d.categoryId === cat.id).slice(0, 5);
          if (catDesigns.length === 0) return null;
          return (
            <DesignSection key={cat.id}
              title={cat.name}
              subtitle={`${catDesigns.length}+ designs available`}
              designs={catDesigns} accent={accent}
              onSelect={setSelected} whatsapp={branding.whatsapp}
              onViewAll={() => navigate(`/category/${cat.slug}`)}
              banner={cat.bannerUrl}
            />
          );
        }) */}

        {/* ── Bestsellers ── */}
        {/* !searched && bestsellers.length > 0 && (
          <DesignSection
            title="Bestsellers" subtitle="Most loved by our customers"
            designs={bestsellers} accent={accent}
            onSelect={setSelected} whatsapp={branding.whatsapp}
            gold
          />
        ) */}

        {/* ── Infinite scroll sentinel ── */}
        {!searched && !loading && (
          <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
        )}

        {/* ── Loading more indicator ── */}
        {!searched && loadingMore && (
          <div style={{ textAlign: "center", padding: "24px" }}>
            <Spinner />
          </div>
        )}

        {/* ── End of designs message ── */}
        {!searched && !hasMore && !loading && designs.length > 0 && (
          <div style={{ textAlign: "center", padding: "32px 24px", color: B.textLight, fontFamily: fonts.body, fontSize: 13 }}>
            — You've seen all designs —
          </div>
        )}

        <SiteFooter branding={branding} categories={categories} />
        <StickyCallButton phone={branding.phone} />

      </main>

      <ProductModal design={selected} onClose={() => setSelected(null)} whatsapp={branding.whatsapp} />
    </>
  );
}

// ─── Category Tile ────────────────────────────────────────────────────────────
function CategoryTile({ cat, accent, delay, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? accent : B.white,
        border: `1.5px solid ${hov ? accent : B.borderLight}`,
        borderRadius: 14, padding: "18px 12px", textAlign: "center", cursor: "pointer",
        transition: "all 0.25s", animation: `fadeUp 0.5s ${delay}ms both`,
        boxShadow: hov ? `0 8px 24px ${accent}33` : "0 2px 8px rgba(0,0,0,0.04)",
      }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{cat.icon}</div>
      <div style={{ fontWeight: 600, fontSize: 12, color: hov ? "#fff" : B.text, fontFamily: fonts.body, lineHeight: 1.3 }}>{cat.name}</div>
      {cat.bannerUrl && (
        <div style={{ position: "absolute", inset: 0, borderRadius: 14, overflow: "hidden", zIndex: -1 }}>
          <img src={withCloudinaryCategoryTint(cat.bannerUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.15 }} />
        </div>
      )}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle, accent, onViewAll, gold }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
      <div>
        <h2 style={{ fontFamily: fonts.display, fontSize: 26, fontWeight: 700, color: B.text, margin: 0 }}>
          {gold && <span style={{ marginRight: 8 }}>⭐</span>}{title}
        </h2>
        {subtitle && <p style={{ fontSize: 13, color: B.textLight, marginTop: 4, fontFamily: fonts.body }}>{subtitle}</p>}
      </div>
      {onViewAll && (
        <button onClick={onViewAll}
          style={{ background: "none", border: `1.5px solid ${accent || B.orange}`, borderRadius: 8, padding: "7px 18px", color: accent || B.orange, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
          View All →
        </button>
      )}
    </div>
  );
}

// ─── Design Grid ─────────────────────────────────────────────────────────────
function DesignGrid({ designs, onSelect, whatsapp }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 24 }}>
      {designs.map((d, i) => (
        <div key={d.id} style={{ animation: `fadeUp 0.5s ${i * 50}ms both` }}>
          <ProductCard design={d} onClick={onSelect} whatsapp={whatsapp} />
        </div>
      ))}
    </div>
  );
}

// ─── Design Section ───────────────────────────────────────────────────────────
function DesignSection({ title, subtitle, designs, accent, onSelect, whatsapp, onViewAll, banner, gold }) {
  return (
    <section style={{ position: "relative", overflow: "hidden" }}>
      {/* Background banner tint */}
      {banner && (
        <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
          <img src={withCloudinaryCategoryTint(banner)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.04 }} />
        </div>
      )}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 24px", position: "relative", zIndex: 1 }}>
        <SectionHeader title={title} subtitle={subtitle} accent={accent} onViewAll={onViewAll} gold={gold} />
        <DesignGrid designs={designs} onSelect={onSelect} whatsapp={whatsapp} />
      </div>
      <Divider />
    </section>
  );
}